import { useEffect, useState } from 'react';
import { useHistoryDismiss } from './use-history-dismiss.js';
import { api, call } from '../platform/api.js';
import { instantFromWallClock, wholeDayRange } from '@mononoke/shared';
import { t } from '../platform/i18n.js';
import { OptionSheet, anchorOf, type AnchorRect } from './option-sheet.js';
import { DayInput } from './day-input.js';
import type { ClientRow } from '../../../api/src/catalog/bookings.js';

/**
 * Creating a booking — the form, extracted from the Bookings screen so the ITEMS screen
 * can open it (maintainer request, 30 Jul 2026).
 *
 * WHY IT MOVED. A booking is a set of garments promised to someone, and the set is what
 * you have in your hands: *"we create the bookings inside the item list… when we select
 * items, we have the option to create a booking"*. The old flow ran the other way —
 * create an empty booking on one screen, walk to another, select, add — and its own
 * success message admitted it: *"Booking created ✓ — add items from the Items screen"*.
 * A confirmation that tells you to go somewhere else is a flow apologising for itself.
 *
 * The Bookings screen becomes a dashboard and loses the form; this component is the
 * form's new and only home.
 *
 * WITH ITEMS, IN ONE ACT. `itemIds` are added immediately after creation, in the same
 * handler. If the add fails the booking still exists and the message says so rather than
 * pretending — a half-made booking you can see and finish beats a rollback that loses
 * the dates you just typed.
 */
export function BookingForm({
  itemIds,
  onCreated,
  onCancel,
}: {
  /** Items to put in it. Empty is legal — an unfilled booking is a real thing to make. */
  itemIds: string[];
  /** Called with the user-facing summary after a successful create. */
  onCreated: (summary: string, bookingId: string) => void;
  onCancel: () => void;
}): JSX.Element {
  // Back / edge-swipe closes the form instead of popping the route (S2 part 3).
  useHistoryDismiss(onCancel);
  const [clients, setClients] = useState<ClientRow[] | null>(null);
  const [title, setTitle] = useState('');
  /**
   * Who the reservation is for. `[]` = nobody, which is legal — an internal hold.
   *
   * ⚠️ ORDER IS MEANING (040): the FIRST client is the one `bookings.client_id` keeps, and
   * therefore the one the reservation summary and check-out mail are addressed to. The
   * caption under the field says so, because a list that looked unordered would make the
   * recipient arbitrary.
   */
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [clientAnchor, setClientAnchor] = useState<AnchorRect | null>(null);
  const [clientOpen, setClientOpen] = useState(false);
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  /**
   * Whole-day or timed (ruling B). Whole-day is the DEFAULT because it is what the
   * atelier books most of the time; times are a deliberate switch, not a form everyone
   * fills in twice.
   */
  const [timed, setTimed] = useState(false);
  const [startsAtLocal, setStartsAtLocal] = useState('');
  const [endsAtLocal, setEndsAtLocal] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      // Not cached, deliberately: this is the picker for an act that cannot be performed
      // offline, and caching the input to an unavailable act furnishes a form with
      // nowhere to submit.
      const result = await call('listClients', () => api.listClients.query());
      if (result.ok) setClients(result.data);
      else setProblem(result.failure.message);
    })();
  }, []);

  // A full 'YYYY-MM-DDTHH:mm', not merely non-empty: the split date/hour fields (0.1.177)
  // can produce half a value — an hour with no date composes 'T09:00' — and a half value
  // must keep Save disabled exactly as an empty one did.
  const wholeStamp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
  // WHOLE DAYS ARE OPTIONAL since 0.1.517 (091, maintainer 30 Sep 2026: "dates are not mandatory for
  // a reservation to be made, to be confirmed, and to be sent out"). Either day may stay empty — an
  // open bound, and the garments are still held. A TIMED window still needs both stamps: a time of
  // day with no day, or one end timed and the other open, is not something anybody meant.
  const rangeReady = timed ? wholeStamp.test(startsAtLocal) && wholeStamp.test(endsAtLocal) : true;
  const n = itemIds.length;

  const submit = async (): Promise<void> => {
    if (busy || title.trim() === '' || !rangeReady) return;
    setBusy(true);
    setProblem(null);

    // The wire carries INSTANTS either way. Whole-day goes through the same shared
    // `wholeDayRange` the server's own tests use, so "the 10th to the 14th" cannot mean
    // one thing here and another there; timed bounds are read as ATELIER wall clock, so
    // a phone in another zone still books Antwerp time.
    let range: { startsAt: string | null; endsAt: string | null };
    try {
      range = timed
        ? { startsAt: instantFromWallClock(startsAtLocal), endsAt: instantFromWallClock(endsAtLocal) }
        : startsOn !== '' && endsOn !== ''
          ? wholeDayRange(startsOn, endsOn)
          : // One side, or none: each bound from its own day through the same shared helper, so an
            // open-ended "from the 10th" starts at exactly the midnight a full range would.
            {
              startsAt: startsOn === '' ? null : wholeDayRange(startsOn, startsOn).startsAt,
              endsAt: endsOn === '' ? null : wholeDayRange(endsOn, endsOn).endsAt,
            };
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'those dates do not make a range');
      setBusy(false);
      return;
    }

    const created = await call('createBooking', () =>
      api.createBooking.mutate({
        title: title.trim(),
        ...range,
        clientIds,
      }),
    );
    if (!created.ok) {
      setProblem(created.failure.message);
      setBusy(false);
      return;
    }

    const booking = created.data.bookings.find((b) => b.title === title.trim());
    if (n === 0 || booking === undefined) {
      setBusy(false);
      onCreated(`Reservation “${title.trim()}” created ✓`, booking?.id ?? '');
      return;
    }

    const added = await call('addBookingItems', () =>
      api.addBookingItems.mutate({ bookingId: booking.id, itemIds }),
    );
    setBusy(false);
    if (!added.ok) {
      // The booking EXISTS. Saying so beats a bare failure that leaves the user
      // wondering whether to type it all again.
      setProblem(`Reservation created, but the items did not go in: ${added.failure.message}`);
      return;
    }
    const warnings = added.data.warnings;
    onCreated(
      `Reservation “${title.trim()}” created with ${String(added.data.added)} item${added.data.added === 1 ? '' : 's'} ✓` +
        (warnings.length > 0
          ? ` — ⚠ ${String(warnings.length)} clash${warnings.length === 1 ? '' : 'es'} with other bookings (shown on the booking)`
          : ''),
      booking.id,
    );
  };

  // In the STORED order, not the list's: the first is the mail's recipient.
  const chosenNames = clientIds
    .map((id) => (clients ?? []).find((c) => c.id === id)?.name)
    .filter((name): name is string => name !== undefined);

  return (
    <div className="sheet booking-form" role="dialog" aria-label="New reservation">
      <header className="sheet-header">
        <strong>{n === 0 ? 'New reservation' : `New reservation · ${String(n)} item${n === 1 ? '' : 's'}`}</strong>
        <button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </header>

      {problem !== null && <p role="alert">{problem}</p>}

      {clientOpen && (
        <OptionSheet
          anchor={clientAnchor}
          title="Clients"
          options={(clients ?? []).map((c) => c.name)}
          selected={chosenNames}
          multi
          /* No free text: a client is a ROW with an address the mails go to, so the ADD row
             below writes one rather than letting a name float free. */
          onChange={(values) => {
            setClientIds(
              values
                .map((name) => (clients ?? []).find((c) => c.name === name)?.id)
                .filter((id): id is string => id !== undefined),
            );
          }}
          onClose={() => setClientOpen(false)}
          manage={{
            noun: 'client',
            radiusFor: (value) => {
              const found = (clients ?? []).find((c) => c.name === value);
              if (found === undefined) return 'this client could not be found — reload first';
              return found.email === null || found.email === ''
                ? 'no email on file — they cannot be sent anything'
                : found.email;
            },
            /** Writes a real row — a client has to exist before a reservation can point at
             *  it. With no email, which the radius line says for every client and the mail
             *  refuses by name; the Clients tab is where an address is added. */
            add: async (name) => {
              const created = await call('createClient', () =>
                api.createClient.mutate({ name, email: undefined, phone: undefined, company: undefined }),
              );
              if (!created.ok) return created.failure.message;
              setClients(created.data.clients);
              // Appended, never first: the first client is the mail's recipient, and adding
              // somebody must not silently redirect a message.
              setClientIds((was) => [...was, created.data.id]);
              return null;
            },
            rename: async (value, next) => {
              const found = (clients ?? []).find((c) => c.name === value);
              if (found === undefined) return 'this client could not be found — reload first';
              if (next === value) return null;
              const renamed = await call('updateClient', () =>
                api.updateClient.mutate({
                  id: found.id,
                  name: next,
                  // Left as they are: the sheet has one field, and sending `undefined` for
                  // the rest must not read as "clear them".
                  email: found.email ?? undefined,
                  phone: found.phone ?? undefined,
                  company: found.company ?? undefined,
                }),
              );
              if (!renamed.ok) return renamed.failure.message;
              setClients(renamed.data.clients);
              return null;
            },
            /** ✕ takes them off THIS reservation. It does not delete the client — that is a
             *  two-mode decision with a confirm that counts what would go, and it lives on
             *  the Clients tab. */
            remove: async (value) => {
              const found = (clients ?? []).find((c) => c.name === value);
              if (found === undefined) return 'this client could not be found — reload first';
              setClientIds((was) => was.filter((id) => id !== found.id));
              return Promise.resolve(null);
            },
          }}
        />
      )}

      <p className="form-grid">
        <label>
          {t('bookings.title')}
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        {/*
          ⚠️ A PICKER, NOT A <select> — 040. A native select is single-value by construction,
          and this field became multi. It is also the same component the reservation detail
          and the boxes tab use, which is the point: three screens picking the same kind of
          thing should not offer three different gestures for it.
        */}
        <label>
          {t('bookings.client')}
          <button
            type="button"
            className="picker-trigger"
            aria-label={`Clients — ${chosenNames.length === 0 ? 'none' : chosenNames.join(', ')}`}
            onClick={(e) => {
              setClientAnchor(anchorOf(e));
              setClientOpen(true);
            }}
          >
            {chosenNames.length === 0 ? t('bookings.noClient') : chosenNames.join(', ')}
          </button>
          {/* Order is invisible in a comma-separated list and it decides who gets emailed. */}
          {chosenNames.length > 1 && <small>Emails go to {chosenNames[0]}, the first in the list.</small>}
        </label>
        {timed ? (
          /**
           * A DATE FIELD AND AN HOUR FIELD, not `datetime-local` (maintainer, 6 Aug 2026:
           * "only open the calendar date picker, no hour picker"). Desktop Chrome's
           * datetime-local popup is a CALENDAR — the hour half is keyboard spinners inside
           * the field, which reads as "there is no hour picker" because visually there
           * is none. A separate `type="time"` input gets its own dropdown of hours, and
           * Firefox — which never grew a datetime-local popup at all — gets two fields it
           * genuinely supports. The state stays one 'YYYY-MM-DDTHH:mm' string; these are
           * two windows onto its halves.
           */
          <>
            <label>
              {t('bookings.startsAt')}
              <DayInput
                value={startsAtLocal.slice(0, 10)}
                onChange={(e) => setStartsAtLocal(`${e.target.value}T${startsAtLocal.slice(11) || '09:00'}`)}
              />
            </label>
            <label>
              Hour
              <input
                type="time"
                value={startsAtLocal.slice(11)}
                onChange={(e) => setStartsAtLocal(`${startsAtLocal.slice(0, 10)}T${e.target.value}`)}
              />
            </label>
            <label>
              {t('bookings.endsAt')}
              <DayInput
                value={endsAtLocal.slice(0, 10)}
                onChange={(e) => setEndsAtLocal(`${e.target.value}T${endsAtLocal.slice(11) || '18:00'}`)}
              />
            </label>
            <label>
              Hour
              <input
                type="time"
                value={endsAtLocal.slice(11)}
                onChange={(e) => setEndsAtLocal(`${endsAtLocal.slice(0, 10)}T${e.target.value}`)}
              />
            </label>
          </>
        ) : (
          <>
            <label>
              {t('bookings.firstDay')}
              <DayInput value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
            </label>
            <label>
              {t('bookings.lastDay')}
              <DayInput value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
            </label>
          </>
        )}
      </p>

      <p className="form-actions">
        <button type="button" onClick={() => setTimed((v) => !v)}>
          {timed ? t('bookings.useWholeDays') : t('bookings.useTimes')}
        </button>{' '}
        <button
          type="button"
          className="btn-primary"
          disabled={busy || title.trim() === '' || !rangeReady}
          onClick={() => void submit()}
        >
          {busy ? t('item.saving') : t('bookings.create')}
        </button>
      </p>
    </div>
  );
}
